import { useState, useEffect } from 'react';
import { Calendar, ExternalLink, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageLoader } from '@shared/components/PageLoader';
import { connectsAPI } from '@/utils/supabase/client';
import type { DailyDigest } from '@/types/connects';
import { format, parseISO } from 'date-fns';

export function DigestArchive() {
  const [digests, setDigests] = useState<DailyDigest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    connectsAPI.getDigests()
      .then(({ digests: d }) => setDigests(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 font-serif">
          <Calendar className="h-6 w-6" />
          Daily Digests
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Archive of daily intelligence briefs written to Google Drive
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>
      )}

      {digests.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No digests yet — they'll appear here after Cowork's 6pm run writes to Drive.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {digests.map((d) => {
            const stats = d.stats as Record<string, unknown>;
            return (
              <Card key={d.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="text-center w-14 shrink-0">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      {format(parseISO(d.date), 'MMM')}
                    </p>
                    <p className="text-2xl font-bold leading-none font-serif">{format(parseISO(d.date), 'd')}</p>
                    <p className="text-xs text-muted-foreground">{format(parseISO(d.date), 'yyyy')}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{format(parseISO(d.date), 'EEEE, MMMM d')}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {typeof stats.items_ingested === 'number' && (
                        <Badge variant="outline" className="text-xs">{stats.items_ingested} items</Badge>
                      )}
                      {typeof stats.contacts_touched === 'number' && (
                        <Badge variant="outline" className="text-xs">{stats.contacts_touched} contacts</Badge>
                      )}
                      {typeof stats.companies_touched === 'number' && (
                        <Badge variant="outline" className="text-xs">{stats.companies_touched} companies</Badge>
                      )}
                    </div>
                  </div>
                  {d.drive_url && (
                    <a
                      href={d.drive_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FileText className="h-4 w-4" />
                      Open doc
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
